import React from 'react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';
import { Badge } from './ui/badge';
import { AlertTriangle, ArrowRight } from 'lucide-react';

const TYPE_LABEL = {
  casual: 'Casual',
  sick: 'Sick',
  annual: 'Privilege',
  special: 'Special',
  unpaid: 'Leave Without Pay (LWP)',
};

const TYPE_TONE = {
  casual: 'bg-blue-50 text-blue-700 border-blue-200',
  sick: 'bg-rose-50 text-rose-700 border-rose-200',
  annual: 'bg-purple-50 text-purple-700 border-purple-200',
  special: 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200',
  unpaid: 'bg-amber-50 text-amber-700 border-amber-200',
};

export default function LeaveSplitConfirmDialog({
  open,
  onOpenChange,
  preview,           // The response body from /api/leaves/preview-split
  onConfirm,         // Called when user confirms — receives nothing
  busy = false,
}) {
  if (!preview) return null;

  const { reason, split, in_probation, total_days, requested_type, snapshot } = preview;
  const requestedLabel = TYPE_LABEL[requested_type] || requested_type;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-lg" data-testid="leave-split-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle size={18} className="text-amber-500" />
            {in_probation ? 'Leave will be marked as LWP (Probation)' : 'Leave will be split with LWP'}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-slate-600">
            {reason}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {/* Split breakdown */}
        <div className="border rounded-lg overflow-hidden">
          <div className="bg-slate-50 px-3 py-2 text-xs font-medium text-slate-600 border-b">
            How your {total_days} day(s) of {requestedLabel} leave will be recorded:
          </div>
          <div className="divide-y">
            {split.map((seg, idx) => (
              <div key={idx} className="px-3 py-2 flex items-center justify-between" data-testid={`split-row-${idx}`}>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className={`text-[10px] font-semibold ${TYPE_TONE[seg.leave_type] || ''}`}>
                    {TYPE_LABEL[seg.leave_type] || seg.leave_type}
                  </Badge>
                  <ArrowRight size={12} className="text-slate-400" />
                </div>
                <span className="text-sm font-semibold text-slate-700">
                  {seg.days} day{seg.days === 1 ? '' : 's'}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Current balance hint */}
        {snapshot?.available && !in_probation && requested_type in snapshot.available && (
          <div className="text-xs text-slate-500 border-t pt-3">
            Current balance for <span className="font-medium">{requestedLabel}</span>:{' '}
            <span className="font-semibold text-slate-700">{snapshot.available[requested_type]}</span> day(s) remaining
            of <span className="font-semibold">{snapshot.quota[requested_type]}</span> annual quota.
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy} data-testid="split-cancel-btn">Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={busy}
            onClick={(e) => { e.preventDefault(); onConfirm(); }}
            className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="split-confirm-btn"
          >
            {busy ? 'Submitting…' : 'Confirm & Submit'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
